import { Response, NextFunction } from 'express';
import { AuthRequest } from './auth.middleware';
import { prisma } from '../shared/prisma';
import { AppError } from '../shared/errors/AppError';

const PERMISSION_MAP: Record<string, string[]> = {
  super_admin: ['*'],
  admin: ['*'],
  team_owner: ['manage_channels', 'send_messages', 'create_channels'],
  member: ['send_messages', 'create_channels'],
  guest: ['send_messages'],
};

export function requirePermission(...permissions: string[]) {
  return async (req: AuthRequest, _res: Response, next: NextFunction) => {
    try {
      if (!req.user) throw new AppError(401, 'Unauthorized');

      const userRoles = await prisma.userRole.findMany({
        where: { userId: req.user.sub },
        include: { role: true },
      });

      const userPermissions = new Set<string>();
      for (const ur of userRoles) {
        const perms = PERMISSION_MAP[ur.role.name] ?? [];
        perms.forEach((p) => userPermissions.add(p));
      }

      if (userPermissions.has('*')) return next();

      const hasAll = permissions.every((p) => userPermissions.has(p));
      if (!hasAll) throw new AppError(403, 'Insufficient permissions');

      next();
    } catch (err) {
      next(err);
    }
  };
}

export function requireRole(...roles: string[]) {
  return async (req: AuthRequest, _res: Response, next: NextFunction) => {
    try {
      if (!req.user) throw new AppError(401, 'Unauthorized');

      const userRoles = await prisma.userRole.findMany({
        where: { userId: req.user.sub },
        include: { role: true },
      });

      const roleNames = userRoles.map((ur) => ur.role.name);
      const hasRole = roles.some((r) => roleNames.includes(r));
      if (!hasRole) throw new AppError(403, 'Role not permitted');

      next();
    } catch (err) {
      next(err);
    }
  };
}
